import { useCallback, useEffect, useState } from 'react'
import { api, ErroApi } from '../lib/api'
import { useSessao } from '../lib/sessao'
import { Secao, Folha, Erro, Carregando, Vazio } from '../componentes/ui'
import './equipe-acessos.css'

/**
 * Quem trabalha aqui — e o que cada um enxerga.
 *
 * Até hoje a clínica era um sistema de UM usuário: o dono criado no
 * cadastro. A tabela de convite existia no banco e nenhuma tela a
 * usava, então a saída prática era dividir a senha — o que transforma
 * em ficção a trilha de auditoria, a produtividade por funcionário e a
 * assinatura do prontuário.
 *
 * O desenho aqui parte de uma distinção que quase nenhum concorrente
 * faz explicitamente: **profissional agendável** e **usuário do
 * sistema** são coisas diferentes. Há quem atenda e nunca entre (o
 * fisio que vem duas vezes por semana), e quem entre e nunca atenda (a
 * recepção). A lista mostra os dois no mesmo lugar, com selos distintos,
 * porque na cabeça de quem administra é uma equipe só.
 */

type Membro = {
  usuario_id: string; nome: string; email: string
  papel: string; ativo: boolean
  ultimo_login: string | null
  profissional_id: string | null; profissional_nome: string | null
  /* ── ficha de quem não atende ─────────────────────────────────────
     Recepção e financeiro não são recurso da agenda, então nada disso
     cabe em `profissional`. Vive no vínculo com a clínica, porque a
     mesma pessoa pode ser recepção aqui e financeiro em outra. */
  cargo: string | null; setor: string | null; telefone: string | null
  admissao: string | null; desligamento: string | null
  emergencia: string | null; observacao: string | null
  /* As duas perguntas independentes, resolvidas pela view. */
  atende: boolean; tem_acesso: boolean
}

type Concessao = {
  usuario_id: string; escopo: string; efeito: 'CONCEDER' | 'NEGAR'
  abrangencia: string; motivo: string | null
  expira_em: string | null; concedido_em: string
}

type Convite = {
  id: string; email: string; papel: string
  expira_em: string; criado_em: string
  convidado_por: string | null; expirado: boolean
}

const PAPEIS: Array<{ id: string; nome: string; explica: string }> = [
  { id: 'DONO',         nome: 'Dono',         explica: 'acesso total, inclusive à conta e ao plano' },
  { id: 'GESTOR',       nome: 'Gestor',       explica: 'tudo da operação: agenda, pacientes, dinheiro e configuração' },
  { id: 'RECEPCAO',     nome: 'Recepção',     explica: 'agenda e pacientes; vê valores, mas não o prontuário' },
  { id: 'PROFISSIONAL', nome: 'Profissional', explica: 'a própria agenda e o prontuário de quem ele atende' },
  { id: 'FINANCEIRO',   nome: 'Financeiro',   explica: 'dinheiro e relatórios; sem prontuário' },
  { id: 'AUDITOR',      nome: 'Auditor',      explica: 'só leitura de trilha e relatórios' },
]

const ESCOPOS: Array<{ id: string; nome: string }> = [
  { id: 'agenda:read',      nome: 'Ver agenda' },
  { id: 'agenda:write',     nome: 'Marcar e remarcar' },
  { id: 'paciente:read',    nome: 'Ver pacientes' },
  { id: 'paciente:write',   nome: 'Cadastrar pacientes' },
  { id: 'prontuario:read',  nome: 'Ler prontuário' },
  { id: 'prontuario:write', nome: 'Escrever no prontuário' },
  { id: 'financeiro:read',  nome: 'Ver o financeiro' },
  { id: 'financeiro:write', nome: 'Lançar e dar baixa' },
  { id: 'relatorio:read',   nome: 'Ver relatórios' },
  { id: 'config:write',     nome: 'Mudar configurações' },
  { id: 'usuario:write',    nome: 'Gerenciar a equipe' },
  { id: 'auditoria:read',   nome: 'Ver a trilha de acesso' },
]

const SETORES: Array<{ id: string; nome: string }> = [
  { id: 'RECEPCAO',       nome: 'Recepção' },
  { id: 'FINANCEIRO',     nome: 'Financeiro' },
  { id: 'ADMINISTRATIVO', nome: 'Administrativo' },
  { id: 'CLINICO',        nome: 'Clínico' },
  { id: 'MARKETING',      nome: 'Marketing' },
  { id: 'APOIO',          nome: 'Apoio' },
]

const nomeSetor  = (s: string | null) => SETORES.find((x) => x.id === s)?.nome ?? null
const nomePapel  = (p: string) => PAPEIS.find((x) => x.id === p)?.nome ?? p
const nomeEscopo = (e: string) => ESCOPOS.find((x) => x.id === e)?.nome ?? e

const quando = (iso: string | null) => {
  if (!iso) return 'nunca entrou'
  const d = new Date(iso)
  const dias = Math.floor((Date.now() - d.getTime()) / 86_400_000)
  if (dias === 0) return 'hoje'
  if (dias === 1) return 'ontem'
  if (dias < 30) return `há ${dias} dias`
  return d.toLocaleDateString('pt-BR')
}

export function EquipeAcessos({ podeEditar }: { podeEditar: boolean }) {
  const { estado } = useSessao()
  const eu = estado.fase === 'dentro' ? estado.ctx.usuarioId : null

  const [membros, setMembros] = useState<Membro[]>([])
  const [concessoes, setConcessoes] = useState<Concessao[]>([])
  const [convites, setConvites] = useState<Convite[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [convidando, setConvidando] = useState(false)
  const [ajustando, setAjustando] = useState<Membro | null>(null)
  /* Quem ATENDE mas ainda não está ligado a nenhum login.
     Sem esta tela, `profissional.usuario_id` só se preenchia por SQL —
     e é ele que faz `/v1/me/contexto` devolver `profissionalId`, que é
     o que impede a pessoa de assinar evolução com o conselho da colega. */
  const [ligando, setLigando] = useState<Membro | null>(null)
  const [profsLivres, setProfsLivres] = useState<Array<{ id: string; nome: string; conselho: string | null }>>([])
  const [tokenNovo, setTokenNovo] = useState<{ email: string; link: string } | null>(null)

  const carregar = useCallback(async () => {
    setErro(null)
    try {
      const eq = await api<{ itens: Membro[]; concessoes: Concessao[] }>('/v1/equipe')
      setMembros(eq.itens); setConcessoes(eq.concessoes)

      /* Profissionais que ainda não têm login associado. `usuario_id`
         não vem na listagem, então cruzo pelo que a view já disse: quem
         está em `equipe` com `profissional_id` já tem dono. */
      const pr = await api<{ itens: Array<{ id: string; nome: string; conselho: string | null; ativo: boolean }> }>(
        '/v1/profissionais').catch(() => ({ itens: [] }))
      const comDono = new Set(eq.itens.map((m) => m.profissional_id).filter(Boolean))
      setProfsLivres(pr.itens.filter((x) => x.ativo && !comDono.has(x.id))
        .map((x) => ({ id: x.id, nome: x.nome, conselho: x.conselho })))

      if (podeEditar) {
        const cv = await api<{ itens: Convite[] }>('/v1/convites')
        setConvites(cv.itens)
      }
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar a equipe.')
    } finally { setCarregando(false) }
  }, [podeEditar])
  useEffect(() => { void carregar() }, [carregar])

  const doMembro = (id: string) => concessoes.filter((c) => c.usuario_id === id)

  async function revogarConvite(id: string) {
    try { await api(`/v1/convites/${id}`, { metodo: 'DELETE' }); await carregar() }
    catch (e) { setErro(e instanceof ErroApi ? e.message : 'Não foi possível revogar.') }
  }

  return (
    <>
      <Secao
        titulo="Quem trabalha aqui"
        descricao="Cada pessoa entra com a própria conta. Senha compartilhada faz toda ação virar “foi o dono”."
        {...(podeEditar
          ? { acao: <button className="botao botao--pequeno"
                            onClick={() => setConvidando(true)}>Convidar</button> }
          : {})}
      >
        <Erro mensagem={erro} />

        {carregando ? <Carregando altura={160} /> : membros.length === 0 ? (
          <Vazio mensagem="Ninguém na equipe ainda." />
        ) : (
          <ul className="eqa__lista">
            {membros.map((m) => {
              const extras = doMembro(m.usuario_id)
              const papel = PAPEIS.find((p) => p.id === m.papel)
              return (
                <li key={m.usuario_id} className={m.ativo ? '' : 'is-inativo'}>
                  <div className="eqa__quem">
                    <strong>{m.nome}</strong>
                    <span className="ajuda">
                      {m.cargo || m.email}
                      {m.setor && ` · ${nomeSetor(m.setor)}`}
                    </span>
                  </div>

                  <div className="eqa__papel">
                    <span className="selo selo--neutro">{nomePapel(m.papel)}</span>
                    {/* Os dois selos abaixo são a distinção que quase
                        ninguém faz na interface: atender e ter login são
                        coisas separadas. */}
                    {m.atende
                      ? <span className="selo selo--ok">atende</span>
                      : <span className="selo eqa__selo-off">não atende</span>}
                    {!m.ativo && <span className="selo selo--erro">sem acesso</span>}
                    {papel && <span className="ajuda eqa__explica">{papel.explica}</span>}
                    {extras.length > 0 && (
                      <span className="eqa__extras">
                        {extras.map((c) => (
                          <span key={c.escopo + c.efeito}
                                className={`selo ${c.efeito === 'NEGAR' ? 'selo--erro' : 'selo--atencao'}`}
                                title={c.motivo ?? undefined}>
                            {c.efeito === 'NEGAR' ? '− ' : '+ '}{nomeEscopo(c.escopo)}
                            {c.abrangencia === 'proprio' && ' (só o dele)'}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>

                  <div className="eqa__dir">
                    <span className="ajuda">{quando(m.ultimo_login)}</span>
                    {/* Ligar o login a um profissional vale para QUALQUER
                        um, inclusive para si mesmo — quem cria a clínica
                        é o primeiro a precisar, e era exatamente quem não
                        tinha nenhum botão. */}
                    {podeEditar && !m.atende && profsLivres.length > 0 && (
                      <button className="botao botao--fantasma botao--pequeno"
                              onClick={() => setLigando(m)}>Ligar a um profissional</button>
                    )}
                    {podeEditar && m.usuario_id !== eu && (
                      <button className="botao botao--fantasma botao--pequeno"
                              onClick={() => setAjustando(m)}>Ajustar</button>
                    )}
                    {m.usuario_id === eu && <span className="ajuda">você</span>}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </Secao>

      {podeEditar && convites.length > 0 && (
        <Secao titulo="Convites pendentes"
               descricao="O link vale 72 horas e serve uma vez só.">
          <ul className="eqa__convites">
            {convites.map((c) => (
              <li key={c.id}>
                <div>
                  <strong>{c.email}</strong>
                  <span className="ajuda">
                    {nomePapel(c.papel)} · convidado por {c.convidado_por ?? '—'}
                  </span>
                </div>
                {c.expirado
                  ? <span className="selo selo--erro">expirado</span>
                  : <span className="selo selo--atencao">aguardando</span>}
                <button className="botao botao--fantasma botao--pequeno"
                        onClick={() => void revogarConvite(c.id)}>Revogar</button>
              </li>
            ))}
          </ul>
        </Secao>
      )}

      {convidando && (
        <FolhaConvite
          aoFechar={() => setConvidando(false)}
          aoConvidar={async (t) => { setConvidando(false); setTokenNovo(t); await carregar() }}
        />
      )}

      {tokenNovo && (
        <Folha
          titulo="Convite criado"
          descricao={`Mande este link para ${tokenNovo.email}. Ele aparece uma única vez.`}
          aoFechar={() => setTokenNovo(null)}
          acoes={<button className="botao" onClick={() => setTokenNovo(null)}>Entendi</button>}
        >
          {/*
            O token não é guardado em claro em lugar nenhum — o banco tem
            só o hash. Se esta tela fechar sem copiar, o caminho é revogar
            e convidar de novo. É deliberado: link de acesso recuperável
            é link que vaza no histórico de alguém.
          */}
          <label className="oculto-visual" htmlFor="cv-link">Link do convite</label>
          <input id="cv-link" className="entrada mono" readOnly value={tokenNovo.link}
                 onFocus={(e) => e.currentTarget.select()} />
          <p className="ajuda">
            Guardamos apenas o resumo criptográfico do link. Se ele se perder, revogue
            o convite e crie outro.
          </p>
        </Folha>
      )}

      {ligando && (
        <LigarProfissional
          membro={ligando}
          opcoes={profsLivres}
          aoFechar={() => setLigando(null)}
          aoLigar={async () => { setLigando(null); await carregar() }} />
      )}

      {ajustando && (
        <FolhaAjuste
          membro={ajustando}
          concessoes={doMembro(ajustando.usuario_id)}
          aoFechar={() => setAjustando(null)}
          aoSalvar={async () => { setAjustando(null); await carregar() }}
        />
      )}
    </>
  )
}

/* ── convidar ───────────────────────────────────────────────────── */

function FolhaConvite({ aoFechar, aoConvidar }: {
  aoFechar(): void
  aoConvidar(t: { email: string; link: string }): Promise<void>
}) {
  const [email, setEmail] = useState('')
  const [papel, setPapel] = useState('RECEPCAO')
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  async function submeter(e: React.FormEvent) {
    e.preventDefault()
    setErro(null); setEnviando(true)
    try {
      const r = await api<{ token: string; email: string }>('/v1/convites', {
        metodo: 'POST', corpo: { email: email.trim(), papel },
      })
      const link = `${window.location.origin}/convite?t=${r.token}`
      await aoConvidar({ email: r.email, link })
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Não foi possível convidar.')
      setEnviando(false)
    }
  }

  const escolhido = PAPEIS.find((p) => p.id === papel)!

  return (
    <Folha
      titulo="Convidar para a equipe"
      descricao="A pessoa define a própria senha ao aceitar. Você nunca digita a senha de outro."
      aoFechar={aoFechar}
      acoes={
        <>
          <button type="button" className="botao botao--fantasma" onClick={aoFechar}>Cancelar</button>
          <button className="botao" type="submit" form="form-convite"
                  disabled={enviando || !email.includes('@')}>
            {enviando ? 'Criando…' : 'Criar convite'}
          </button>
        </>
      }
    >
      <form id="form-convite" onSubmit={submeter} className="eqa__form">
        <Erro mensagem={erro} />
        <div className="campo">
          <label htmlFor="cv-email">E-mail</label>
          <input id="cv-email" className="entrada" type="email" required autoFocus
                 inputMode="email" value={email}
                 onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="cv-papel">Papel</label>
          <select id="cv-papel" className="entrada" value={papel}
                  onChange={(e) => setPapel(e.target.value)}>
            {PAPEIS.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
          <span className="ajuda">{escolhido.explica}</span>
        </div>
      </form>
    </Folha>
  )
}

/* ── ajustar papel e acessos ────────────────────────────────────── */

function FolhaAjuste({ membro, concessoes, aoFechar, aoSalvar }: {
  membro: Membro
  concessoes: Concessao[]
  aoFechar(): void
  aoSalvar(): Promise<void>
}) {
  const [papel, setPapel] = useState(membro.papel)
  const [ativo, setAtivo] = useState(membro.ativo)
  const [cargo, setCargo] = useState(membro.cargo ?? '')
  const [setor, setSetor] = useState(membro.setor ?? '')
  const [telefone, setTelefone] = useState(membro.telefone ?? '')
  const [admissao, setAdmissao] = useState(membro.admissao ?? '')
  const [emergencia, setEmergencia] = useState(membro.emergencia ?? '')
  const [escopo, setEscopo] = useState('')
  const [motivo, setMotivo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  async function salvarVinculo() {
    setErro(null); setSalvando(true)
    try {
      // Só o que foi preenchido: string vazia não é "apague", e mandar
      // '' onde o banco espera date dá erro de tipo.
      const corpo: Record<string, unknown> = { papel, ativo }
      const talvez = (k: string, v: string) => { if (v.trim()) corpo[k] = v.trim() }
      talvez('cargo', cargo)
      talvez('setor', setor)
      talvez('telefone', telefone)
      talvez('admissao', admissao)
      talvez('emergencia', emergencia)
      await api(`/v1/equipe/${membro.usuario_id}`, { metodo: 'PATCH', corpo })
      await aoSalvar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível salvar.')
      setSalvando(false)
    }
  }

  async function conceder(efeito: 'CONCEDER' | 'NEGAR') {
    if (!escopo) return
    setErro(null)
    try {
      await api(`/v1/equipe/${membro.usuario_id}/escopos`, {
        metodo: 'POST',
        corpo: { escopo, efeito, ...(motivo.trim() ? { motivo: motivo.trim() } : {}) },
      })
      setEscopo(''); setMotivo('')
      await aoSalvar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível alterar o acesso.')
    }
  }

  async function remover(c: Concessao) {
    try {
      await api(`/v1/equipe/${membro.usuario_id}/escopos/${c.escopo}?efeito=${c.efeito}`,
                { metodo: 'DELETE' })
      await aoSalvar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível remover.')
    }
  }

  return (
    <Folha
      titulo={membro.nome}
      descricao={membro.email}
      aoFechar={aoFechar}
      acoes={
        <>
          <button type="button" className="botao botao--fantasma" onClick={aoFechar}>Fechar</button>
          <button className="botao" onClick={() => void salvarVinculo()} disabled={salvando}>
            {salvando ? 'Salvando…' : 'Salvar'}
          </button>
        </>
      }
    >
      <div className="eqa__form">
        <Erro mensagem={erro} />

        <div className="campo">
          <label htmlFor="aj-papel">Papel</label>
          <select id="aj-papel" className="entrada" value={papel}
                  onChange={(e) => setPapel(e.target.value)}>
            {PAPEIS.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
          <span className="ajuda">{PAPEIS.find((p) => p.id === papel)?.explica}</span>
        </div>

        <label className="eqa__switch">
          <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
          <span>
            <strong>Tem acesso ao sistema</strong>
            <span className="ajuda">
              Desligar não apaga nada: o histórico e a trilha continuam com o nome dela.
            </span>
          </span>
        </label>

        {/*
          A ficha de quem NÃO atende. Recepção e financeiro não são
          recurso da agenda, então nada disso caberia em `profissional` —
          mora no vínculo com esta clínica. A mesma pessoa pode ser
          recepção aqui e financeiro em outra, com datas diferentes.
        */}
        <hr className="eqa__hr" />
        <h3 className="eqa__h3">Ficha</h3>

        <div className="eqa__dupla">
          <div className="campo">
            <label htmlFor="aj-cargo">Cargo</label>
            <input id="aj-cargo" className="entrada" maxLength={60}
                   placeholder="recepcionista, auxiliar…"
                   value={cargo} onChange={(e) => setCargo(e.target.value)} />
          </div>
          <div className="campo">
            <label htmlFor="aj-setor">Setor</label>
            <select id="aj-setor" className="entrada" value={setor}
                    onChange={(e) => setSetor(e.target.value)}>
              <option value="">—</option>
              {SETORES.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          </div>
        </div>

        <div className="eqa__dupla">
          <div className="campo">
            <label htmlFor="aj-tel">Telefone</label>
            <input id="aj-tel" className="entrada mono" inputMode="tel" maxLength={20}
                   value={telefone} onChange={(e) => setTelefone(e.target.value)} />
          </div>
          <div className="campo">
            <label htmlFor="aj-adm">Admissão</label>
            <input id="aj-adm" className="entrada" type="date"
                   value={admissao} onChange={(e) => setAdmissao(e.target.value)} />
          </div>
        </div>

        <div className="campo">
          <label htmlFor="aj-emerg">Em caso de emergência, avisar</label>
          <input id="aj-emerg" className="entrada" maxLength={120}
                 placeholder="nome e telefone"
                 value={emergencia} onChange={(e) => setEmergencia(e.target.value)} />
        </div>

        <hr className="eqa__hr" />

        {/*
          A "recepcionista de confiança" mora aqui: papel RECEPCAO mais
          um acesso pontual, com motivo e autoria registrados. Antes, a
          única saída era promovê-la a Gestor — o que entregava o
          financeiro inteiro e as configurações junto.
        */}
        <h3 className="eqa__h3">Acessos além do papel</h3>

        {concessoes.length === 0 ? (
          <p className="ajuda">Nenhum. Esta pessoa tem exatamente o que o papel dá.</p>
        ) : (
          <ul className="eqa__concessoes">
            {concessoes.map((c) => (
              <li key={c.escopo + c.efeito}>
                <span className={`selo ${c.efeito === 'NEGAR' ? 'selo--erro' : 'selo--atencao'}`}>
                  {c.efeito === 'NEGAR' ? 'sem' : 'com'}
                </span>
                <div>
                  <strong>{nomeEscopo(c.escopo)}</strong>
                  {c.motivo && <span className="ajuda"> · {c.motivo}</span>}
                  <span className="ajuda eqa__bloco">
                    desde {new Date(c.concedido_em).toLocaleDateString('pt-BR')}
                    {c.expira_em && ` · até ${new Date(c.expira_em).toLocaleDateString('pt-BR')}`}
                  </span>
                </div>
                <button className="eqa__x" onClick={() => void remover(c)}
                        aria-label={`Remover ${nomeEscopo(c.escopo)}`}>×</button>
              </li>
            ))}
          </ul>
        )}

        <div className="campo">
          <label htmlFor="aj-escopo">Adicionar ou tirar um acesso</label>
          <select id="aj-escopo" className="entrada" value={escopo}
                  onChange={(e) => setEscopo(e.target.value)}>
            <option value="">Escolha…</option>
            {ESCOPOS.map((x) => <option key={x.id} value={x.id}>{x.nome}</option>)}
          </select>
        </div>
        <div className="campo">
          <label htmlFor="aj-motivo">Por quê</label>
          <input id="aj-motivo" className="entrada" maxLength={200} value={motivo}
                 placeholder="fica registrado junto com quem autorizou"
                 onChange={(e) => setMotivo(e.target.value)} />
        </div>
        <div className="eqa__dupla">
          <button type="button" className="botao botao--pequeno"
                  disabled={!escopo} onClick={() => void conceder('CONCEDER')}>Dar acesso</button>
          <button type="button" className="botao botao--fantasma botao--pequeno"
                  disabled={!escopo} onClick={() => void conceder('NEGAR')}>Tirar acesso</button>
        </div>
      </div>
    </Folha>
  )
}

/**
 * Liga um login a um profissional já cadastrado.
 *
 * ─── POR QUE ISTO FALTAVA E DOÍA ──────────────────────────────────
 *
 * `profissional.usuario_id` existe na tabela e as rotas de POST e PATCH
 * sempre aceitaram o campo — nenhuma TELA oferecia. Só se preenchia por
 * SQL direto.
 *
 * É esse vínculo que faz `/v1/me/contexto` devolver `profissionalId`, e
 * é o `profissionalId` que:
 *  · impede a pessoa de assinar evolução com o conselho da colega
 *    (documento imutável, oito vezes por dia);
 *  · faz "Meu dia" e "minha comissão" saberem de quem são.
 *
 * Quem cria a clínica é o primeiro a precisar disto — e era justamente
 * quem não tinha botão nenhum na própria linha.
 */
function LigarProfissional({ membro, opcoes, aoFechar, aoLigar }: {
  membro: Membro
  opcoes: Array<{ id: string; nome: string; conselho: string | null }>
  aoFechar(): void
  aoLigar(): Promise<void>
}) {
  const [escolhido, setEscolhido] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  async function ligar() {
    if (!escolhido) return
    setSalvando(true); setErro(null)
    try {
      await api(`/v1/profissionais/${escolhido}`, {
        metodo: 'PATCH', corpo: { usuario_id: membro.usuario_id },
      })
      await aoLigar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível ligar.')
      setSalvando(false)
    }
  }

  return (
    <>
      <div className="painel__fundo" onClick={aoFechar} />
      <div className="porque" role="dialog" aria-modal="true"
           aria-label="Ligar login a um profissional">
        <h3>Quem {membro.nome} é na agenda?</h3>
        <p className="ajuda">
          Ligar o login a um profissional é o que faz o sistema saber de quem é a
          agenda, a comissão e — sobretudo — a assinatura da evolução. Sem isso,
          a pessoa precisa escolher em nome de quem assina, e pode errar.
        </p>

        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        <div className="campo">
          <label htmlFor="lig-prof">Profissional</label>
          <select id="lig-prof" className="entrada" value={escolhido}
                  onChange={(e) => setEscolhido(e.target.value)}>
            <option value="">— escolha —</option>
            {opcoes.map((o) => (
              <option key={o.id} value={o.id}>
                {o.nome}{o.conselho && o.conselho !== 'NENHUM' ? ` · ${o.conselho}` : ''}
              </option>
            ))}
          </select>
          <span className="ajuda">
            Só aparecem os que ainda não têm login associado. Se o profissional
            não estiver na lista, cadastre-o em Ajustes › Quem trabalha aqui.
          </span>
        </div>

        <div className="porque__acoes">
          <button className="botao botao--fantasma" onClick={aoFechar}>Voltar</button>
          <button className="botao" disabled={!escolhido || salvando} onClick={() => void ligar()}>
            {salvando ? 'Ligando…' : 'Ligar'}
          </button>
        </div>
      </div>
    </>
  )
}
